/**
 * `GET /kontroledge/fwaas/:id/policies` (CTX-1163): the firewall's rules, in the order
 * the firewall evaluates them, with how much each one is used.
 *
 * Rules point at objects BY NAME; no address, mask or comment reaches the browser. A
 * name the backend found an address in travels as `null` and is shown as hidden —
 * never trimmed or guessed.
 */
export interface FirewallPolicy {
    id: number;
    name: string | null;
    enabled: boolean;
    /** Interfaces and zones. */
    from: (string | null)[];
    to: (string | null)[];
    /** Address objects and groups. */
    sources: (string | null)[];
    destinations: (string | null)[];
    services: (string | null)[];
    action: "accept" | "deny" | null;
    /** `null` on a disabled rule: FortiOS keeps no counters for it. */
    hits: number | null;
    bytes: number | null;
    active_sessions: number | null;
    /** `null` when never used, or when the firmware does not report it (7.6, most rules). */
    last_used_at: string | null;
}

export interface FirewallPolicies {
    items: FirewallPolicy[];
    implicit_deny_hits: number | null;
}

/** An enabled rule FortiOS counts as never matched since its counters were reset. */
export const isNeverUsed = (policy: FirewallPolicy) => policy.enabled && policy.hits === 0;

/**
 * A rule worth a second look: on but never matched, or turned off. The table's filter
 * and the "Rules to review" item of Observations count the same rules (CTX-1208).
 */
export const isToReview = (policy: FirewallPolicy) => isNeverUsed(policy) || !policy.enabled;

export type PolicyFilter = "all" | "to-review";
